import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import JsBarcode from 'jsbarcode';

import wwLogo from '#client/assets/images/ww-logo-shoeteria.png';
import { formatDateTime, formatPhoneNumber } from '#client/utils/helpers';

// US Letter portrait: stores print on Letter, and a three-column table fits far
// more rows per page upright than the previous A4 landscape did.
const PAGE_FORMAT = 'letter';
const MARGIN = 36;
const BODY_FONT_SIZE = 9;
const TITLE_FONT_SIZE = 14;
const LINE_HEIGHT = 11;
const LOGO_WIDTH = 130;
const LOGO_HEIGHT = 38;
const BARCODE_WIDTH = 200;
const BARCODE_HEIGHT = 44;
const COLUMN_WIDTH = 210;
// Room under the table for the page number, and above it for the continuation band.
const FOOTER_HEIGHT = 44;
const CONTINUATION_TOP = MARGIN + 26;
// Reserved under the table so the total always lands on the last page of line
// items rather than spilling onto a page of its own.
const TOTAL_BLOCK_HEIGHT = 34;

// Helvetica's ascender as a fraction of font size. pdf.text places a baseline,
// so this is how far above it the visible top of a line of text sits.
const ASCENDER_RATIO = 0.72;

const COLUMN_TOP = MARGIN + 38;

/**
 * Page one header geometry, in top-down points. The logo's top edge meets the
 * top of the label text beside it, and still clears the rule under the title.
 */
export const HEADER_LAYOUT = {
    ruleY: MARGIN + 24,
    logoY: COLUMN_TOP - BODY_FONT_SIZE * ASCENDER_RATIO,
    columnTop: COLUMN_TOP,
};

/**
 * Total quantity sent across every line of a transfer.
 */
export function sumSentQty(lines = []) {
    return lines.reduce((total, line) => total + (line.qtySent ?? 0), 0);
}

/**
 * Carrier followed by one tracking number per line. A transfer can carry several
 * comma-separated numbers and each needs its own line to stay readable.
 */
export function formatTrackingLines(carrier, trackingNumber) {
    const numbers = String(trackingNumber ?? '')
        .split(',')
        .map((number) => number.trim())
        .filter(Boolean);

    return carrier ? [carrier, ...numbers] : numbers;
}

/**
 * A store rendered as the lines of a mailing address.
 */
export function formatAddressLines(location = {}) {
    const { name, address = {} } = location;
    const { address1, city, province, zip, country, phone } = address;
    const cityLine = [city, province].filter(Boolean).join(', ');

    return [name, address1, [cityLine, zip].filter(Boolean).join(' '), country, formatPhoneNumber(phone)].filter(Boolean);
}

/** Bold label followed by its value lines. Returns the y it finished on. */
function drawLabelledBlock(pdf, label, lines, x, y) {
    pdf.setFont('helvetica', 'bold');
    pdf.text(label, x, y);
    pdf.setFont('helvetica', 'normal');

    let cursor = y;
    for (const line of lines) {
        cursor += LINE_HEIGHT;
        pdf.text(String(line), x, cursor);
    }

    return cursor;
}

function drawRule(pdf, y) {
    pdf.setDrawColor(204, 204, 204);
    pdf.setLineWidth(0.5);
    pdf.line(MARGIN, y, pdf.internal.pageSize.getWidth() - MARGIN, y);
}

/**
 * Page one: transfer id, logo, ship date, tracking, both addresses and the
 * barcode. Returns the y the line-item table can start at.
 */
function drawFullHeader(pdf, transfer, { logoDataUrl, barcodeDataUrl }) {
    const { displayTransferId, locationFrom, locationTo, carrier, trackingNumber, dateOfTransfer } = transfer;
    const pageWidth = pdf.internal.pageSize.getWidth();

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(TITLE_FONT_SIZE);
    pdf.text(`Transfer Shipment #${displayTransferId}`, MARGIN, MARGIN + 12);

    drawRule(pdf, HEADER_LAYOUT.ruleY);

    if (logoDataUrl) {
        pdf.addImage(logoDataUrl, 'PNG', pageWidth - MARGIN - LOGO_WIDTH, HEADER_LAYOUT.logoY, LOGO_WIDTH, LOGO_HEIGHT, undefined, 'FAST');
    }

    pdf.setFontSize(BODY_FONT_SIZE);
    const columnTop = HEADER_LAYOUT.columnTop;

    const shipDateEnd = drawLabelledBlock(pdf, 'Ship Date:', [dateOfTransfer ? formatDateTime(dateOfTransfer) : 'Not yet in transit'], MARGIN, columnTop);
    const shipFromEnd = drawLabelledBlock(pdf, 'Ship From:', formatAddressLines(locationFrom), MARGIN, shipDateEnd + LINE_HEIGHT + 4);

    const trackingEnd = drawLabelledBlock(pdf, 'Transfer Tracking #:', formatTrackingLines(carrier, trackingNumber), MARGIN + COLUMN_WIDTH, columnTop);
    const shipToEnd = drawLabelledBlock(pdf, 'Ship To:', formatAddressLines(locationTo), MARGIN + COLUMN_WIDTH, trackingEnd + LINE_HEIGHT + 4);

    let cursor = Math.max(shipFromEnd, shipToEnd) + LINE_HEIGHT;

    if (barcodeDataUrl) {
        pdf.addImage(barcodeDataUrl, 'PNG', MARGIN, cursor, BARCODE_WIDTH, BARCODE_HEIGHT, undefined, 'FAST');
        cursor += BARCODE_HEIGHT;
    }

    drawRule(pdf, cursor + 6);

    return cursor + 16;
}

/**
 * Continuation pages carry the transfer id and both stores on one band, so a
 * reader can identify a loose page without spending half of it on addresses.
 */
function drawContinuationHeader(pdf, transfer) {
    const { displayTransferId, locationFrom, locationTo } = transfer;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(BODY_FONT_SIZE);
    pdf.text(`Transfer Shipment #${displayTransferId}`, MARGIN, MARGIN + 8);

    pdf.setFont('helvetica', 'normal');
    pdf.text(`Ship From: ${locationFrom?.name ?? ''}`, MARGIN + COLUMN_WIDTH, MARGIN + 8);
    pdf.text(`Ship To: ${locationTo?.name ?? ''}`, MARGIN + COLUMN_WIDTH * 2, MARGIN + 8);

    drawRule(pdf, MARGIN + 14);
}

/** Whole-transfer total, drawn once under the table on the final page. */
function drawTotalQty(pdf, transfer) {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let y = (pdf.lastAutoTable?.finalY ?? MARGIN) + 20;

    if (y + LINE_HEIGHT > pageHeight - FOOTER_HEIGHT) {
        pdf.addPage();
        drawContinuationHeader(pdf, transfer);
        y = CONTINUATION_TOP + 20;
    }

    pdf.setFontSize(BODY_FONT_SIZE);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Total Qty:', pageWidth - MARGIN - 60, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(String(sumSentQty(transfer.lines)), pageWidth - MARGIN, y, { align: 'right' });
}

/** Page numbers need the final page count, so they are a pass of their own. */
function drawPageNumbers(pdf) {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const totalPages = pdf.getNumberOfPages();

    for (let page = 1; page <= totalPages; page++) {
        pdf.setPage(page);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(BODY_FONT_SIZE);
        pdf.text(`Page ${page} of ${totalPages}`, pageWidth - MARGIN, pageHeight - MARGIN + 4, { align: 'right' });
    }
}

/**
 * Draws a transfer's packing slip as real PDF text. Line items go through
 * autoTable, which paginates, repeats the column headers and keeps a row whole
 * across a page break.
 */
export function buildPackingSlipDoc(transfer, { logoDataUrl = null, barcodeDataUrl = null } = {}) {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: PAGE_FORMAT, compress: true });
    const tableTop = drawFullHeader(pdf, transfer, { logoDataUrl, barcodeDataUrl });

    autoTable(pdf, {
        // Qty carries its own halign: a columnStyles halign reaches the body cells
        // only, which would leave the heading adrift from the numbers under it.
        head: [['Item #', 'Description', { content: 'Qty', styles: { halign: 'right' } }]],
        body: transfer.lines.map((line) => [line.sku ?? '', line.title ?? '', String(line.qtySent ?? 0)]),
        startY: tableTop,
        margin: { top: CONTINUATION_TOP, right: MARGIN, bottom: FOOTER_HEIGHT + TOTAL_BLOCK_HEIGHT, left: MARGIN },
        styles: { fontSize: BODY_FONT_SIZE, cellPadding: 4, overflow: 'linebreak', font: 'helvetica' },
        headStyles: { fontSize: BODY_FONT_SIZE, fillColor: [241, 241, 241], textColor: [32, 34, 35], fontStyle: 'bold' },
        columnStyles: {
            0: { cellWidth: 100 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 44, halign: 'right' },
        },
        // Repeat the column headers and never let a row straddle a page break.
        showHead: 'everyPage',
        rowPageBreak: 'avoid',
        didDrawPage: (data) => {
            if (data.pageNumber > 1) drawContinuationHeader(pdf, transfer);
        },
    });

    drawTotalQty(pdf, transfer);
    drawPageNumbers(pdf);

    return pdf;
}

let cachedLogoDataUrl = null;

// An image that neither loads nor errors would leave the caller waiting forever,
// and the print button pending with it. Give up and print without the logo.
const LOGO_TIMEOUT_MS = 3000;

/**
 * The logo as a data URL. Resolves to null if it cannot be read in time, so a
 * missing image costs the slip its logo rather than the whole download.
 */
async function getLogoDataUrl() {
    if (cachedLogoDataUrl) return cachedLogoDataUrl;

    return new Promise((resolve) => {
        let settled = false;
        const settle = (value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(value);
        };
        const timer = setTimeout(() => settle(null), LOGO_TIMEOUT_MS);

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                canvas.getContext('2d').drawImage(img, 0, 0);
                cachedLogoDataUrl = canvas.toDataURL('image/png');
                settle(cachedLogoDataUrl);
            } catch {
                settle(null);
            }
        };
        img.onerror = () => settle(null);
        img.src = wwLogo;
    });
}

/** CODE128 barcode of the transfer id, or null if it cannot be drawn. */
function getBarcodeDataUrl(code) {
    if (!code) return null;

    try {
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, String(code), {
            format: 'CODE128',
            width: 2,
            height: 40,
            margin: 4,
            displayValue: true,
            fontSize: 14,
        });
        return canvas.toDataURL('image/png');
    } catch {
        return null;
    }
}

/** Download name for a transfer's slip. */
export function packingSlipFilename(transfer) {
    return `PackingSlip_${transfer.displayTransferId}.pdf`;
}

/**
 * Builds a transfer's packing slip and hands it to the browser as a download.
 * Returns the document so callers can assert on it. The only caller is
 * handlePackingSlip in the transfer store.
 */
export async function generatePackingSlipPDF(transfers) {
    const transfer = transfers?.[0];

    if (!transfer) throw new Error('No transfer to print.');

    const [logoDataUrl, barcodeDataUrl] = await Promise.all([getLogoDataUrl(), Promise.resolve(getBarcodeDataUrl(transfer.displayTransferId))]);

    const pdf = buildPackingSlipDoc(transfer, { logoDataUrl, barcodeDataUrl });

    pdf.save(packingSlipFilename(transfer));

    return pdf;
}
